import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  VStack,
  useColorModeValue,
  Text,
  Spinner,
  Button,
  HStack,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tag,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PrivateNavbar } from "../layout/PrivateNavbar";
import { Footer } from "../layout/Footer";
import { paymentsAPI } from "../../services";
import { FiCode, FiDollarSign, FiPlus } from "react-icons/fi";
import { Payment } from "types";

export const Payments: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const tableBg = useColorModeValue("white", "gray.800");
  const [selectedQR, setSelectedQR] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { i18n } = useTranslation();

  useEffect(() => {
    const loadPayments = async () => {
      try {
        const data = await paymentsAPI.getUserPayments();
        setPayments(data);
      } catch (err) {
        console.error("Error loading payments:", err);
        setError(t("payments.error.loading"));
      } finally {
        setIsLoading(false);
      }
    };

    loadPayments();
  }, [t]);

  const hasPendingPayments = payments.some(
    (payment) => payment.status === "PENDING"
  );

  const getStatusColor = (status: Payment["status"]) => {
    switch (status) {
      case "PAID":
        return "green";
      case "PENDING":
        return "yellow";
      case "FAILED":
        return "red";
      case "CANCELLED":
        return "gray";
      default:
        return "gray";
    }
  };

  const getPaymentMethod = (paymentMethod: string) => {
    switch (paymentMethod) {
      case "efipay-card":
        return "Cartão";
      case "efipay-billet":
        return "Boleto";
      case "efipay-pix":
        return "Pix";
      case "efipay-link":
        return "Link";
      default:
        return "---";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(
      i18n.language === "pt-BR" ? "pt-BR" : "en-US"
    );
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const handlePendingPayment = (payment: Payment) => {
    if (payment.paymentUrl) {
      window.location.href = payment.paymentUrl;
    }
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PrivateNavbar />

      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            <Box
              display="flex"
              flexDirection={{ base: "column", md: "row" }}
              justifyContent={{ base: "center", md: "space-between" }}
              alignItems={{ base: "flex-start", md: "center" }}
              gap={4}
            >
              <Heading
                size="xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text"
              >
                {t("payments.history.title")}
              </Heading>
              <Button
                leftIcon={<FiPlus />}
                colorScheme="brand"
                onClick={() => navigate("/payments/add-funds")}
              >
                {t("payments.addFunds.title")}
              </Button>
            </Box>

            {isLoading ? (
              <Box textAlign="center" py={10}>
                <Spinner size="xl" color="brand.primary" />
              </Box>
            ) : error ? (
              <Box textAlign="center" py={10}>
                <Text color="red.500">{error}</Text>
              </Box>
            ) : payments.length === 0 ? (
              <Box textAlign="center" py={10}>
                <Text>{t("payments.history.empty")}</Text>
              </Box>
            ) : (
              <Box bg={tableBg} shadow="base" rounded="lg" overflow="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>{t("payments.history.date")}</Th>
                      <Th>{t("payments.history.description")}</Th>
                      <Th>{t("payments.history.type")}</Th>
                      <Th>{t("payments.history.amount")}</Th>
                      <Th>{t("payments.history.status")}</Th>
                      <Th>{t("payments.history.actions")}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {payments.map((payment) => (
                      <Tr key={payment.id}>
                        <Td>{formatDate(payment.createdAt)}</Td>
                        <Td>
                          <Text fontSize="sm">{payment.description} </Text>
                          <Tag colorScheme="teal">
                            {getPaymentMethod(payment.paymentMethod!)}
                          </Tag>
                        </Td>
                        <Td>
                          <Badge colorScheme="purple">
                            {t(`payments.types.${payment.type.toLowerCase()}`)}
                          </Badge>
                        </Td>
                        <Td>
                          {formatAmount(payment.amount, payment.currency)}
                        </Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(payment.status)}>
                            {t(
                              `payments.status.${payment.status.toLowerCase()}`
                            )}
                          </Badge>
                        </Td>
                        <Td>
                          {payment.status === "PENDING" && (
                            <HStack spacing={2}>
                              {payment.paymentUrl && (
                                <Button
                                  size="sm"
                                  leftIcon={<FiDollarSign />}
                                  colorScheme="yellow"
                                  onClick={() => handlePendingPayment(payment)}
                                >
                                  {t("payments.pending.payNow")}
                                </Button>
                              )}
                              {payment.qrCodeImage && (
                                <Button
                                  size="sm"
                                  colorScheme="green"
                                  //   leftIcon={<FiCode />}
                                  onClick={() => {
                                    setSelectedQR(payment.qrCodeImage!);
                                    onOpen();
                                  }}
                                >
                                  Pix
                                </Button>
                              )}
                            </HStack>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </VStack>
        </Container>
      </Box>

      <Footer />

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("payments.qrcode.title")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Image src={selectedQR!} alt="QR Code" mx="auto" />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};
